/**
 * MovieExploreActions Component
 * Action buttons for movie constellation exploration
 */

import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMovieExploreStore } from '../../stores/movie-explore-store';
import { generateMovieExplore } from '../../services/movie-explore.service';
import MovieSaveConstellationModal from './MovieSaveConstellationModal';
import { wanderingModal, WanderingModal } from '../WanderingModal';

export default function MovieExploreActions() {
  const {
    nodes,
    vibe,
    results,
    loading,
    isValid,
    setLoading,
    setError,
    setExploreResults,
    clearConstellation,
    clearResults,
  } = useMovieExploreStore();

  const [showSaveModal, setShowSaveModal] = useState(false);
  const hasResults = results.length > 0;

  const handleAction = async (action: string, label: string) => {
    if (!isValid()) {
      toast.error('Add at least 3 nodes to generate recommendations');
      return;
    }

    wanderingModal.show();
    setLoading(true);
    setError(null);

    try {
      const response = await generateMovieExplore({
        nodes,
        vibe,
        action: action as any,
        previous_results: results.map((r) => `${r.title} (${r.year})`),
      });

      if (response.success) {
        setExploreResults(response);
        toast.success(`${label}! Found ${response.data.recommendations.length} films.`);
      } else {
        throw new Error(response.error || `Failed to ${label.toLowerCase()}`);
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error?.message || err?.message || 'An error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      wanderingModal.hide();
    }
  };

  const handleGenerate = () => handleAction('generate', 'Found films');
  const handleGoDeeper = () => handleAction('go_deeper', 'Went deeper');
  const handleTighten = () => handleAction('tighten', 'Tightened focus');
  const handleByMovement = () => handleAction('by_movement', 'By movement');
  const handleByDirector = () => handleAction('by_director', 'By director');

  const handleBranch = () => {
    clearResults();
    toast.success('Constellation branched! Modify nodes or vibe and generate again.');
  };

  const handleSave = () => {
    if (!isValid()) {
      toast.error('Add at least 3 nodes to save your constellation');
      return;
    }
    setShowSaveModal(true);
  };

  const handleClear = () => {
    clearConstellation();
    toast.success('Constellation cleared');
  };

  return (
    <div className="space-y-3">
      {/* Primary Action: Find Films */}
      <button
        onClick={handleGenerate}
        disabled={!isValid() || loading}
        className="w-full px-6 py-4 bg-blue-600 text-white text-lg font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-3">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12" cy="12" r="10"
                stroke="currentColor" strokeWidth="4" fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Finding films...
          </span>
        ) : (
          'Find Films'
        )}
      </button>

      {/* Secondary Actions - Only show after first generation */}
      {hasResults && (
        <>
          <div className="pt-2 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2 text-center">Refine your results</p>
          </div>

          {/* Refinement Actions */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleGoDeeper}
              disabled={loading}
              className="px-3 py-2 bg-orange-50 text-orange-700 text-sm font-medium rounded-md hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-orange-200"
            >
              🌀 Go Deeper
            </button>
            <button
              onClick={handleTighten}
              disabled={loading}
              className="px-3 py-2 bg-green-50 text-green-700 text-sm font-medium rounded-md hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-green-200"
            >
              🎯 Tighten Focus
            </button>
          </div>

          {/* Deep Dive Actions */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleByMovement}
              disabled={loading}
              className="px-3 py-2 bg-purple-50 text-purple-700 text-sm font-medium rounded-md hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-purple-200"
            >
              🌊 By Movement
            </button>
            <button
              onClick={handleByDirector}
              disabled={loading}
              className="px-3 py-2 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-md hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-indigo-200"
            >
              🎬 By Director
            </button>
          </div>

          {/* Utility Actions */}
          <div className="pt-2 border-t border-gray-200">
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={handleBranch}
                disabled={loading}
                className="px-2 py-2 bg-gray-50 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-gray-200"
              >
                🌿 Branch
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-2 py-2 bg-gray-50 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-gray-200"
              >
                💾 Save
              </button>
              <button
                onClick={handleClear}
                disabled={loading}
                className="px-2 py-2 bg-red-50 text-red-700 text-xs font-medium rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-red-200"
              >
                🗑️ Clear
              </button>
            </div>
          </div>
        </>
      )}

      <MovieSaveConstellationModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
      />

      <WanderingModal allowDismiss onDismiss={() => { setLoading(false); wanderingModal.hide(); }} />
    </div>
  );
}
